import autobind from '../autobind';
import BaseInteraction from '../player/BaseInteraction';
import BasePlugin, { LoadOptions } from '../common/BasePlugin';
import CompositeError from '../common/utils/CompositeError';
import ContentConfig from '../config/ContentConfig';
import ContentItem from '../api/content/ContentItem';
import EventEmitter from '../common/EventEmitter';
import InterceptorStack from '../interceptor/InterceptorStack';
import PlayerType from '../player/PlayerType';
import SDKConfig from '../common/SDKConfig';
import configureReportLogging from '../common/configureReportLogging';
import determineIpType from './determineIpType';
import emitPlaybackStatusChangeEvent from '../common/utils/emitPlaybackStatusChangeEvent';
import extractJWTPayload from '../common/utils/extractJWTPayload';
import gatherPrimingData from '../priming/gatherPrimingData';
import getContentData from './getContentData';
import getPlaybackModule from '../common/utils/getPlaybackModule';
import getPlayerInteraction from './getPlayerInteraction';
import handleContentResponse from './handleContentResponse';
import logger from '../log';
import notifySourcesReady from '../util/notifySourcesReady';
import prepareKollectiveSources from '../common/utils/prepareKollectiveSources';
import setToCDN from '../common/utils/setToCDN';
import sendBitrateRangeEvent from '../util/sendBitrateRangeEvent';
import {
  ApiEvents,
  ErrorMessages,
  PlayerAPI,
  PlayType
} from '@kollective-frontend/ksdk-api';
import { DeprecatedEvents } from '../public/events';
import {
  ContentCallResult,
  KollectiveContentItem,
  SourceItemBlock
} from '../common/interfaces';
import { DeliveryMode } from '../common/constants';
import { Detection } from '../common/Detector/detectForSource';
import { PrimingData } from '../priming/interfaces';
import { ReporterSections } from '../common/Reporter/constants';
import { SourceType } from '../common/constants';
import { dispatchError, dispatchUpdateReportEvent } from '../events/dispatch';
import { isString } from '../common/utils/stringify';
import {
  IPlaybackModuleConstructor,
  OptionalKollectiveSources,
  SessionMetadata
} from '../playback/interfaces';
import stringToPlayType from '../util/stringToPlayType';
import { TechApi } from '../bootloader/tech';
import base64ToJson from '../common/utils/base64ToJson';
import { retryPromise } from '../util/retry';
import { handleMonitoringHlsManifest } from '../interceptor/maybeModifyManifest';

const log = logger.getLogger(__filename);
configureReportLogging(); // Set up report with default settings.

const { NO_PLAYBACK_MODULE } = ErrorMessages;

interface PrimingContentData {
  priming: PrimingData;
}

export const mergeOptions = (
  plugin: KsdkPlugin,
  options: LoadOptions
): void => {
  // Get the contentToken from the options.
  plugin.setContentToken(options.contentToken, options.token);

  // Get the Integrators token
  plugin.integratorToken = options.integratorToken;

  // NOTE: user token is merely a token to identify the user by. It is set on the "user" field in the general report.
  plugin.userToken = options.userToken;
  plugin.userEmail = options.userEmail;

  plugin.legacySourceTypeFallback = options.legacySourceTypeFallback;

  // get option data
  plugin.title = options.contentTitle;
  plugin.contentUrl = options.contentUrl;
  plugin.state.play.sourceType = options.sourceType;

  if (options.videoElement && !plugin.playerInteraction.mediaElement()) {
    plugin.playerInteraction.setVideoElement(options.videoElement);
  }

  if (options.setSource === false) {
    plugin.setSource = options.setSource;
  }
};

export function getInitContainer(options: {
  contentToken?: string;
  integratorToken?: string;
  jitc?: string;
}):
  | { app: string; tenantId: string; contentId: string; contentHost?: string }
  | undefined {
  if (options.contentToken) {
    const contentJWT = extractJWTPayload<{
      app: string;
      tid: string;
      cid: string;
      iss: string;
      contentHost?: string;
    }>(options.contentToken);
    if (!contentJWT) {
      dispatchError(ErrorMessages.INVALID_CONTENT_TOKEN);
      return;
    }
    // return false;
    return {
      app: contentJWT.app,
      tenantId: contentJWT.tid,
      contentId: contentJWT.cid,
      contentHost: contentJWT.contentHost || contentJWT.iss
    };
  } else if (options.integratorToken && options.jitc) {
    const conf = base64ToJson(options.jitc, 'Invalid jitc');
    if (typeof conf === 'undefined' || JSON.stringify(conf) === '{}') {
      dispatchError(ErrorMessages.INVALID_JIT_CONFIG);
      return;
    }
    const integratorPayload = extractJWTPayload<{ sub: string }>(
      options.integratorToken
    );
    if (!integratorPayload) {
      dispatchError(ErrorMessages.INVALID_INTEGRATOR_TOKEN);
      return;
    }

    return {
      app: conf.app,
      tenantId: conf.tenantId,
      contentId: integratorPayload.sub
    };
  } else {
    dispatchError(ErrorMessages.INVALID_LOAD_OPTIONS);
    return;
  }
}

export const mergeInitConfig = (
  plugin: KsdkPlugin,
  options: LoadOptions
): boolean => {
  const initContainer = getInitContainer(options);
  if (!initContainer) {
    // error dispatch handled in getInitContainer.
    return false;
  }
  const config = ContentConfig.getInstance();
  const play = plugin.state.play;
  play.sdkConfig.setIntegration({ name: initContainer.app });
  play.tenantId = initContainer.tenantId;
  play.contentId = initContainer.contentId;

  // Look for the contentHost. This should have been loaded via prewarm config.
  play.contentHost = config.ch || initContainer.contentHost;

  config.app = initContainer.app;
  if (options.config) {
    config.merge(options.config, { from: 'pre-warm' });
  }
  return true;
};

export default class KsdkPlugin extends BasePlugin {
  contentUrl?: string;
  legacySourceTypeFallback?: SourceType;
  playerInteraction: BaseInteraction<unknown>;
  playerOptions: unknown;
  playerType: PlayerType;
  setSource?: boolean;
  masterPlaylistData?: { playlist: string; url: string };
  start?: number;
  title?: string;
  userToken?: string;
  userEmail?: string;
  integrationControlledReset = false;

  constructor(
    playerType: PlayerType,
    player: unknown,
    playerOptions: unknown,
    tech: TechApi
  ) {
    super(new SDKConfig({ name: 'ksdk' }, 20000), player, tech);
    autobind(this);
    this.playerType = playerType;
    this.playerOptions = playerOptions || {};

    this.playerInteraction = getPlayerInteraction(
      player,
      this.playerType,
      this.playerOptions,
      this.tech
    );

    this.setSource = true;

    // set up interceptor
    this.playerInteraction.bindInterceptor(
      () => this.shouldIntercept,
      // getOptions: passes current state information to the bound player interaction
      () => ({
        activeMode: this.state.play.activeMode as DeliveryMode,
        playType: this.state.play.playType,
        sourceType: this.state.play.sourceType as SourceType,
        stack: this.stack as InterceptorStack
      }),
      () => this.integrationControlledReset
    );
  }

  getPlayer(): Promise<PlayerAPI> {
    if (!this.hasLoadStarted) {
      return Promise.reject(new Error('getPlayer called before load'));
    }
    return Promise.resolve(this.playerInteraction.getPlayer() as PlayerAPI);
  }

  // The 10 Playback module expects a `sourceInfo object`. This meets it's desires.
  getSourceInfo(): SourceItemBlock {
    const sources = this.contentSources
      .filter((item: KollectiveContentItem) => !item.data.noLegacy)
      .map(s => ({
        src: s.url,
        type: s.data.mime
      }));
    return { sources, legacySourceTypeFallback: this.legacySourceTypeFallback };
  }

  async load(options: LoadOptions): Promise<BasePlugin> {
    if (options.disabled) {
      const reason = 'Kollective plugin is disabled';
      dispatchError(reason);
      this.switchToCDN(reason);
      return this;
    }
    this.createObjectId = options.createObjectId;
    this.integrationControlledReset =
      options.integrationControlledReset ?? false;
    // Integrator has option of presetting the PlayType.
    this.state.play.playType = stringToPlayType(options.playlistType);
    if (this.state.play.playType !== PlayType.UNKNOWN) {
      this.state.play.playTypeOverridden = true;
    }
    this.hasLoadStarted = true;
    this.start = Date.now();
    if (options.masterPlaylist && options.masterPlaylistUrl) {
      const { masterPlaylist: playlist, masterPlaylistUrl: url } = options;
      this.masterPlaylistData = { playlist, url };
    }

    mergeOptions(this, options);

    // get data from contentToken
    if (!mergeInitConfig(this, options)) {
      // ApiEvents.ERROR - already triggered.
      return this as BasePlugin;
    }

    // Check we have expected state.
    if (!this.state.play.contentHost) {
      dispatchError(ErrorMessages.NO_CONTENT_HOST);
      return this as BasePlugin;
    }

    const config = ContentConfig.getInstance();

    const priming = await gatherPrimingData({
      config,
      eventTitle: this.title,
      userEmail: this.userEmail,
      info: {
        authToken: this.getKollectiveToken() as string,
        contentHost: this.state.play.contentHost,
        contentId: this.state.play.contentId as string,
        tenantId: this.state.play.tenantId as string
      },
      contentHost: this.state.play.contentHost as string,
      iceCandidates: options.iceCandidates,
      getIceCandidates: options.getIceCandidates
    });
    // If we ever need to recheck for iceCandidates we'll put it in the local
    // state so that we can use it.
    this.state.play.getIceCandidates = options.getIceCandidates;
    const data = { type: determineIpType(priming.networkData) };
    const mistypedData = data.type as unknown as Record<string, unknown>;
    EventEmitter.getInstance().emit(DeprecatedEvents.IP_MDNS, mistypedData);
    EventEmitter.getInstance().emit(ApiEvents.IP_TYPE, { data });
    log.debug('locality and networkData', priming);
    // Now we have the contentToken. Let's make a request for config and content
    // metadata.
    await this.pullContentData({ priming });
    return this as BasePlugin;
  }

  async pullContentData(input: PrimingContentData): Promise<void> {
    const { priming } = input;
    const { locality: body } = priming;
    const { play } = this.state;
    const { contentSessionRetry } = this.getConfig();
    if (play.playTypeOverridden && play.playType !== PlayType.UNKNOWN) {
      body.sessionPlayType = play.playType;
    }

    return retryPromise<ContentCallResult, Response>(
      timeout => {
        return getContentData({
          body,
          contentHost: play.contentHost as string,
          contentId: play.contentId as string,
          headers: this.getInitHeaders(),
          tenantId: play.tenantId as string,
          timeout
        });
      },
      {
        shouldRetry: (err: Response) => {
          const matchedStatus = contentSessionRetry.backoffStatusCodes.some(
            code => err.status === code
          );
          return matchedStatus ? true : false;
        },
        initBackoffInterval: contentSessionRetry.initBackoffInterval,
        maxBackoffInterval: contentSessionRetry.maxBackoffInterval,
        jitterFactor: contentSessionRetry.jitterFactor,
        operationTimeout: contentSessionRetry.requestTimeout,
        retryCount: contentSessionRetry.retryCount,
        maxDuration: contentSessionRetry.maxDuration
      }
    )
      .then((res: ContentCallResult) =>
        handleContentResponse(res, priming, this)
      )
      .catch(this.contentCallFail);
  }

  async startLoad(detection: Detection): Promise<void> {
    sendBitrateRangeEvent(
      detection.playType,
      ContentConfig.getInstance()
    ).catch(e => {
      log.error('sendBitrateRangeEvent failed:', e);
      dispatchError('failed to send bitrate range event: ' + e);
    });

    const data = getPlaybackModule(detection.mode);
    const { playback, PlaybackModule: playbackModule, reason } = data;
    dispatchUpdateReportEvent({
      section: ReporterSections.General,
      value: { playback, playType: detection.playType }
    });

    const sessionMetadata: SessionMetadata = {};
    if (this.state.play.playTypeOverridden) {
      // SRV-4668: We only want the sessionPlayType set when overridden by the integrator
      sessionMetadata.sessionPlayType = this.state.play.playType;
    } else {
      this.state.play.playType = detection.playType;
    }
    this.createInterceptorStack();
    this.shouldIntercept = true;
    if (reason || isString(playbackModule)) {
      throw new Error(`${NO_PLAYBACK_MODULE} ${reason || playbackModule}`);
    }
    const PlaybackModule = playbackModule as IPlaybackModuleConstructor;
    this.playbackModule = new PlaybackModule(
      {
        state: this.state,
        createObjectId: this.createObjectId,
        content: {
          token: this.getKollectiveToken() as string,
          items: this.contentSources
        },
        detection,
        sessionMetadata,
        sourceType: this.state.play.sourceType as SourceType,
        switchToCDN: this.switchToCDN,
        fallbackToCDN: this.fallbackToCDN
      },
      this.stack
    );

    await this.playbackModule.init();
    if (this.masterPlaylistData) {
      const { url, playlist } = this.masterPlaylistData;
      handleMonitoringHlsManifest(url, playlist);
    }
    this.state.play.activeMode = this.playbackModule.mode;
    emitPlaybackStatusChangeEvent({
      plugin: this,
      reason: 'Successful playback initiated'
    });
    return;
  }

  /**
   * 10 agent playback specific. This communicates to the 10 agent that it needs to switch to cdn playback.
   */
  switchToCDN(error?: Error | string): void {
    this._toCdn('switchToCDN', error);
  }

  fallbackToCDN(error?: Error | string): void {
    this._toCdn('fallbackToCDN', error);
  }

  _toCdn(name: 'fallbackToCDN' | 'switchToCDN', error?: Error | string): void {
    setToCDN(this, error);
    this.state.play.isKollectiveDisabled = true;
    let sourcesSet = undefined;
    if (this.setSource && this.playerInteraction[name]) {
      sourcesSet = this.playerInteraction[name](this.contentSources);
    }
    this.notifySourcesReady(this.contentSources, sourcesSet, undefined, name);
    const key = name === 'fallbackToCDN' ? 'falling back' : 'switching';
    log.warn(`${this.getContext()} ${key} to cdn playback with error:`, error);
  }

  contentCallFail(error?: Error | string): void {
    if (error && error instanceof CompositeError) {
      const last = error.lastError();
      if (last instanceof XMLHttpRequest && last.status === 0) {
        dispatchError(ErrorMessages.UNABLE_TO_CONNECT_TO_CONTENT);
      }
    }
    this.fallbackToCDN(error);
  }

  playKollective(sources: OptionalKollectiveSources): void {
    // Only ecdn10 uses the sources.
    // TODO - this comes from start delivery, which could be a modified
    // sourceInfo passed to the 10 playback module. we need to standardize source lists
    // or provide a common conversion framework.
    const srcs = prepareKollectiveSources(sources, this.contentSources);

    let sourcesSet = undefined;
    if (this.setSource) {
      sourcesSet = this.playerInteraction.playKollective(srcs);
    }

    // if sources contain kurls, then prepareKollective sources will replace the url on the item.
    const kSources =
      sources && sources.sources && sources.sources.find(s => s.kurl)
        ? srcs
        : undefined;
    this.notifySourcesReady(
      this.contentSources,
      sourcesSet,
      kSources,
      'playKollective'
    );
  }

  notifySourcesReady(
    sources: ContentItem[],
    sourcesSet: ContentItem[] | undefined,
    kollectiveSources: ContentItem[] | undefined,
    reason: string
  ): void {
    const emitter = EventEmitter.getInstance();

    notifySourcesReady(emitter, {
      sourcesSet,
      sources,
      kollectiveSources,
      reason
    });
  }

  /**
   * Returns the video element that was passed in options or found from the player.
   */
  getVideoElement(): HTMLVideoElement | undefined {
    return this.playerInteraction.mediaElement() as HTMLVideoElement;
  }
}
